import { ct } from './i18n.js';
import { toast } from './toast.js';

/**
 * A button that copies the field beside it.
 *
 * Written for the partner page, whose whole purpose is handing somebody a link.
 * A read-only input holding a forty-character URL is a link people select by
 * dragging across it, and half of those selections take one character too few.
 *
 * **The failure is announced.** `navigator.clipboard` is absent on an insecure
 * origin and refused outright by some browsers, and a button that silently does
 * nothing is worse than no button: the reader believes they have the link and
 * pastes whatever was there before.
 */
export function initCopyFields(root: ParentNode = document): void {
  for (const button of root.querySelectorAll<HTMLElement>('[data-copy-target]')) {
    const target = document.getElementById(button.dataset.copyTarget ?? '');
    if (!(target instanceof HTMLInputElement)) continue;

    button.addEventListener('click', () => {
      void copy(target.value);
    });
  }
}

async function copy(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast(ct('client.copied', 'Copied'), 'success');
  } catch {
    /*
     * Never thrown at the page. The clipboard is a permission the browser may
     * refuse for reasons that have nothing to do with this site, and the only
     * useful answer is to tell the reader to do it themselves.
     */
    toast(ct('client.copyFailed', 'Could not copy. Select the link and copy it by hand.'), 'error');
  }
}
